import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/store/auth.store';
import { useTenantStore } from '@/store/tenant.store';
import {
  clientModeHeaders,
  isFieldClient,
  isFieldIngressCall,
  isSessionTimeoutResponse,
} from '@/services/client-mode';
import { isEmbeddedMode } from '@/utils/embedded-console';
import { handleTerminalAuth, terminalAuthCode } from '@/services/terminal-auth';
import { isDeviceSessionTenantMismatch, requestDeviceSessionCheck } from '@/services/device-session-signal';
import { isNativeAuthHydrationSettled } from '@/services/native-auth-hydration-state';
import {
  accessTokenNeedsRefresh,
  isNoRefreshPath,
  isSignInPath,
  refreshConsoleSession,
  SESSION_REFRESH_HEADER,
  wantsConsoleRefresh,
} from '@/services/session-refresh';
import {
  canRefreshDeviceSession,
  deviceTokenNeedsRefresh,
  refreshDeviceSession,
} from '@/services/device-session-refresh';

/** Marks a request already retried after a console refresh (never retried twice). */
type RetriableConfig = InternalAxiosRequestConfig & { _sessionRefreshed?: boolean };

/** A 401 that a console refresh can cure: an expired / unverifiable access token. */
function isRefreshable401(status: number | undefined, data: unknown): boolean {
  if (status !== 401) return false;
  const code = (data as { error?: { code?: unknown } } | undefined)?.error?.code;
  return code === undefined || code === 'INVALID_TOKEN' || code === 'AUTHENTICATION_REQUIRED';
}

export const API_URL = (() => {
  const url = import.meta.env.VITE_API_URL;
  if (!url && import.meta.env.PROD) {
    throw new Error('VITE_API_URL environment variable is not set. The app cannot connect to the backend.');
  }
  return url || 'http://localhost:3000';
})();

/** Per-request options for a write. `tenantId` pins X-Tenant-ID (a stored offline row's own tenant). */
export interface SendConfig {
  signal?: AbortSignal;
  tenantId?: string;
}

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: `${API_URL}/api`,
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 30000,
    });

    // Request interceptor - add auth token, tenant header, and clean empty strings
    this.client.interceptors.request.use(
      async (config) => {
        // P4-07: a refreshable console session renews its 15-minute access
        // token BEFORE sending a request with an expired one (and before any
        // socket reads the store). Never for the sign-in / refresh calls.
        if (!isNoRefreshPath(config.url) && accessTokenNeedsRefresh()) {
          await refreshConsoleSession();
        } else if (!isNoRefreshPath(config.url) && deviceTokenNeedsRefresh()) {
          // W11-BRIDGEKEY: the native bridge's 1-hour device-bound token,
          // renewed with the device session's refresh credential (and its
          // device-key proof when the session is key-bound).
          await refreshDeviceSession();
        }
        // Sign-ins from the web console ask for a refreshable session.
        if (isSignInPath(config.url) && wantsConsoleRefresh()) {
          config.headers[SESSION_REFRESH_HEADER] = '1';
        }
        const token = useAuthStore.getState().token;
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        // A send of a stored offline row pins the row's own tenant (SendConfig.tenantId, from
        // offline/client-id.ts sendTenantOf): a tenant switch between the originator gate and
        // the request can never retarget it. Every other request goes to the active tenant.
        const activeTenant = useTenantStore.getState().activeTenant;
        if (!config.headers['X-Tenant-ID'] && activeTenant) {
          config.headers['X-Tenant-ID'] = activeTenant.id;
        }
        // Native shell or /field routes: exempt from the console idle timeout
        // (see services/client-mode.ts). Evaluated per request, so leaving
        // field mode drops the declaration immediately. Never on SOS,
        // positions, check-ins or session start: exempt by route already, and
        // they must not depend on the API's CORS policy allowing the header.
        if (!isFieldIngressCall(config.method, config.url)) {
          for (const [name, value] of Object.entries(clientModeHeaders())) {
            config.headers[name] = value;
          }
        }
        // Strip empty strings from request bodies so optional fields with format
        // validation (email, phone, etc.) don't fail Zod validation on the backend.
        if (config.data && typeof config.data === 'object' && !(config.data instanceof FormData)) {
          config.data = this.stripEmptyStrings(config.data);
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor - handle errors
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        // A terminal auth code (SESSION_REVOKED, MEMBERSHIP_REMOVED,
        // USER_DISABLED, TOKEN_REVOKED): the credential is dead for good.
        // Sign out and wipe the device's offline field data (P1-12).
        const terminal = terminalAuthCode(error.response?.status, error.response?.data);
        if (terminal) {
          void handleTerminalAuth(terminal);
          return Promise.reject(error);
        }
        // A device-bound token addressed to another tenant (the user switched
        // organisations): register this install there, then the caller retries.
        if (isDeviceSessionTenantMismatch(error.response?.status, error.response?.data)) {
          requestDeviceSessionCheck();
        }
        // P4-07: an expired console access token. Renew the session once and
        // retry the request; only a refresh that is definitively refused
        // falls through to the sign-out below. A transient refresh failure
        // (offline, 5xx) keeps the session: the caller just sees this error.
        const original = error.config as RetriableConfig | undefined;
        if (
          original &&
          !original._sessionRefreshed &&
          !isNoRefreshPath(original.url) &&
          isRefreshable401(error.response?.status, error.response?.data) &&
          (useAuthStore.getState().refreshToken || canRefreshDeviceSession())
        ) {
          const outcome = useAuthStore.getState().refreshToken ? await refreshConsoleSession() : await refreshDeviceSession();
          if (outcome === 'refreshed') {
            original._sessionRefreshed = true;
            original.headers.Authorization = `Bearer ${useAuthStore.getState().token}`;
            return this.client.request(original);
          }
          if (outcome === 'transient') return Promise.reject(error);
        }
        if (error.response?.status === 401) {
          const authState = useAuthStore.getState();
          const isLoginRequest = error.config?.url?.includes('/auth/login');
          const isMeRequest = error.config?.url?.includes('/auth/me');
          // The idle timeout is a console control the server never applies to
          // a field client. If one still reaches a field client (a request
          // sent from a console route that resolved after switching to field
          // mode), keep the token: SOS, positions and check-ins accept it, and
          // the next field request restarts the session's clock. Discarding
          // it would leave the SOS button with nothing to send.
          const isFieldIdleTimeout =
            isSessionTimeoutResponse(error.response.status, error.response.data) && isFieldClient();

          // A refused console handoff code is the /handoff page's own error to show.
          const isHandoffRequest = error.config?.url?.includes('/auth/console-handoff/');

          // Native: until the token has been read from the vault (or while a
          // Keystore read is deferred), a request goes out without it, and its
          // 401 says nothing about the session (P1-12).
          const tokenNotYetLoaded = !isNativeAuthHydrationSettled();

          // Don't auto-logout on login or /me requests (handled separately)
          if (!isLoginRequest && !isMeRequest && !isHandoffRequest && !isFieldIdleTimeout && !tokenNotYetLoaded) {
            // Clear the stored token so it can be refreshed
            authState.logout('expired');

            // Embedded in the native app (the device session was revoked or
            // the console session expired), there is no hard redirect: the
            // logout above already told the shell (useEmbeddedSessionBridge),
            // a navigation now would race that message, and RequireAuth shows
            // /handoff, never a password page inside the WebView.
            if (!isEmbeddedMode() && window.location.pathname !== '/login') {
              // Only hard-redirect if not already on the login page (prevents loop).
              // Carry a reason + returnUrl so the login page can explain why the
              // user landed there ("session expired") and send them back after.
              const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
              window.location.href = `/login?reason=expired&returnUrl=${returnUrl}`;
            }
          }
        }
        return Promise.reject(error);
      }
    );
  }

  private stripEmptyStrings(obj: any): any {
    if (Array.isArray(obj)) {
      return obj.map(item => this.stripEmptyStrings(item));
    }
    if (obj && typeof obj === 'object') {
      const result: any = {};
      for (const [key, value] of Object.entries(obj)) {
        // Only strip empty strings (they fail Zod format validation for email, phone, etc.).
        // Preserve null values -- they represent intentional field clearing (e.g., unsetting
        // a phone number or clearing an assignment).
        if (value === '') continue;
        result[key] = typeof value === 'object' && value !== null ? this.stripEmptyStrings(value) : value;
      }
      return result;
    }
    return obj;
  }

  get<T>(url: string, params?: any, config?: { signal?: AbortSignal }): Promise<T> {
    return this.client.get(url, { params, ...config }).then((res) => res.data);
  }

  /**
   * A FormData body must never go out under the instance-default
   * `Content-Type: application/json` set in the constructor above.
   *
   * axios 1.x's transformRequest checks the *resolved* content type first: if it
   * contains `application/json` and the body is FormData, it replaces the
   * multipart payload with `JSON.stringify(formDataToJSON(data))`. A Blob
   * serializes to `{}`, so every file silently disappears before the request
   * leaves the browser; multer sees zero files and the route answers
   * 400 VALIDATION_ERROR "No files uploaded" — forever, on every retry.
   *
   * That is how offline damage-assessment photo sync lost evidence: the
   * assessment row flipped to is_synced while its storm-damage photos stayed
   * locked in IndexedDB with an upload_error, and were gone for good once the
   * device's storage was cleared. Anything posting FormData through post/put/
   * patch (offline photo sync, sUAS snapshots, attachments) hits the same trap,
   * so detect it here rather than at each call site.
   */
  private isFormData(data: unknown): data is FormData {
    return typeof FormData !== 'undefined' && data instanceof FormData;
  }

  /** Headers for a SendConfig: the pinned tenant of a stored row, when given. */
  private pinned(config?: SendConfig, extra: Record<string, string> = {}): Record<string, string> {
    return config?.tenantId ? { ...extra, 'X-Tenant-ID': config.tenantId } : extra;
  }

  // `config` mirrors get(): callers that re-issue on every map interaction need
  // an AbortSignal to cancel the superseded request.
  post<T>(url: string, data?: any, config?: SendConfig): Promise<T> {
    if (this.isFormData(data)) return this.upload<T>(url, data, config);
    return this.client.post(url, data, { signal: config?.signal, headers: this.pinned(config) }).then((res) => res.data);
  }

  put<T>(url: string, data?: any, config?: SendConfig): Promise<T> {
    if (this.isFormData(data)) {
      return this.client
        .put(url, data, { headers: this.pinned(config, { 'Content-Type': 'multipart/form-data' }) })
        .then((res) => res.data);
    }
    return this.client.put(url, data, { headers: this.pinned(config) }).then((res) => res.data);
  }

  patch<T>(url: string, data?: any, config?: SendConfig): Promise<T> {
    if (this.isFormData(data)) {
      return this.client
        .patch(url, data, { headers: this.pinned(config, { 'Content-Type': 'multipart/form-data' }) })
        .then((res) => res.data);
    }
    return this.client.patch(url, data, { headers: this.pinned(config) }).then((res) => res.data);
  }

  /**
   * DELETE, optionally carrying a request body.
   *
   * A few routes REQUIRE a body on DELETE — the ICS-214 activity-entry delete
   * mandates a `reason`, which the backend writes to `ics214_audit_exceptions`
   * (TEXT NOT NULL) because an ICS 214 is a legal shift record. Axios only puts a
   * body on a DELETE when it is passed through the config's `data` field, so
   * without this parameter such requests went out bodyless, the route's Zod body
   * schema rejected them with a 400, and the operator saw nothing but a generic
   * validation toast while the entry stayed in the log forever.
   *
   * `data: undefined` is inert — the many bodyless DELETE callers are unaffected.
   */
  delete<T>(url: string, data?: any, config?: SendConfig): Promise<T> {
    return this.client.delete(url, { data, headers: this.pinned(config) }).then((res) => res.data);
  }

  upload<T>(url: string, formData: FormData, config?: SendConfig): Promise<T> {
    return this.client
      .post(url, formData, {
        headers: this.pinned(config, { 'Content-Type': 'multipart/form-data' }),
      })
      .then((res) => res.data);
  }

  getBlob(url: string): Promise<Blob> {
    return this.client
      .get(url, { responseType: 'blob' })
      .then((res) => res.data);
  }

  /**
   * Same as getBlob() but keeps the response headers.
   *
   * Export endpoints signal a short payload with X-Export-Truncated; getBlob()
   * throws those headers away, which is how a truncated roster download could
   * still show a plain "Export downloaded" toast. Callers that must not present
   * an incomplete file as complete should use this instead.
   */
  getBlobWithHeaders(url: string): Promise<{ blob: Blob; headers: Record<string, string> }> {
    return this.client
      .get(url, { responseType: 'blob' })
      .then((res) => ({
        blob: res.data as Blob,
        headers: (res.headers ?? {}) as unknown as Record<string, string>,
      }));
  }

  getText(url: string): Promise<string> {
    return this.client
      .get(url, { responseType: 'text' })
      .then((res) => res.data);
  }
}

export const api = new ApiClient();
